import type { ReactNode } from 'react'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { RowActions, type RowAction } from './RowActions'
import { LifecycleError, useLifecycle } from './useLifecycle'
import { formatDue, type LocalDate } from './recurringRules'
import type { Bill, Entry } from './recurringWire'

/** A confirm the page has open: which verb, on which row. */
export type PendingConfirm = { kind: 'skip' | 'delete'; entry: Entry }

const errorFor = (thrown: unknown): string =>
  thrown instanceof LifecycleError ? thrown.message : 'That did not work, and nothing has changed. Try again in a moment.'

/**
 * One row's actions menu with its lifecycle verbs wired (Task 7). Pause and
 * Resume act at once, so the row owns that hook; Skip and Delete only ask the
 * page to open a confirm, so the dialog is mounted once for the page and not
 * once per row.
 */
export function BillRowActions({
  bill,
  entry,
  inline,
  onOpenSheet,
  onConfirm,
}: {
  bill: Bill
  entry: Entry
  inline: boolean
  onOpenSheet: (kind: 'markPaid', period: LocalDate | null) => void
  onConfirm: (confirm: PendingConfirm) => void
}) {
  const { setPaused, busy } = useLifecycle(bill)
  function onAction(kind: RowAction) {
    if (kind === 'skip' || kind === 'delete') onConfirm({ kind, entry })
    // The hook has already toasted a failure and rolled back; nothing to add.
    else void setPaused(kind === 'pause').catch(() => undefined)
  }
  return <RowActions bill={bill} entry={entry} inline={inline} onOpenSheet={onOpenSheet} onAction={onAction} busy={busy} />
}

const COPY = {
  skip: {
    body: 'This skips only this payment. The bill stays active and the next one is still due.',
    confirmLabel: 'Skip payment',
    pendingLabel: 'Skipping…',
    mode: 'skip_recurring',
  },
  delete: {
    title: 'Delete this recurring payment?',
    body: 'Its payment history stays on your transactions.',
    confirmLabel: 'Delete',
    pendingLabel: 'Deleting…',
    mode: 'delete_recurring',
  },
} as const

/**
 * The confirm for Skip and Delete (S9, S10). It stays open on a failure and
 * says so through `errorFor`; the caller closes it on success. Mounted only
 * while a confirm is pending, so the hook's bill is always the row's own.
 */
export function LifecycleConfirm({ confirm, onClose }: { confirm: PendingConfirm; onClose: () => void }): ReactNode {
  const { entry, kind } = confirm
  return kind === 'delete' ? (
    <DeleteConfirm bill={entry.bill} onClose={onClose} />
  ) : (
    <SkipConfirm entry={entry} onClose={onClose} />
  )
}

function SkipConfirm({ entry, onClose }: { entry: Entry; onClose: () => void }): ReactNode {
  const { skip } = useLifecycle(entry.bill)
  return (
    <ConfirmDialog
      open
      {...COPY.skip}
      title={`Skip the ${formatDue(entry.dueDate, 'short')} payment?`}
      errorFor={errorFor}
      onCancel={onClose}
      onConfirm={async () => {
        await skip(entry.dueDate)
        onClose()
      }}
    />
  )
}

/**
 * The delete confirm for one bill. Exported for Bill settings, which has the
 * bill and no schedule entry: the copy, the failure line and the hook are the
 * row menu's own, so the two cannot drift. On success the hook navigates away.
 */
export function DeleteConfirm({ bill, onClose }: { bill: Bill; onClose: () => void }): ReactNode {
  const { remove } = useLifecycle(bill)
  return (
    <ConfirmDialog
      open
      destructive
      {...COPY.delete}
      errorFor={errorFor}
      onCancel={onClose}
      onConfirm={async () => {
        await remove()
        onClose()
      }}
    />
  )
}
